import { useContext, useEffect, useState, useSyncExternalStore, type CSSProperties } from "react";
import { Button } from "../ui/Button";
import { answeredChoices, refusalOf, useAnswer } from "./answer";
import { Results, SmsNumber } from "./Results";
import { Podium } from "./Podium";
import { smsVoting } from "./smsVote";
import { roomLayout, type Viewport } from "./roomLayout";
import { RoomBox } from "./roomBox";
import { CHOICE_TYPES, MOD_ATTR, type LivePayload } from "./types";
import { fallbackOf } from "../settings/catalog";
import { useStructuralSetting, useT, useText } from "../settings/context";

/**
 * ═══ L'ACTIVITÉ EN COURS, CÔTÉ PARTICIPANT ═══
 *
 * Cinq types, un seul écran. Ce qui change entre eux tient à trois questions : le
 * public choisit ou écrit, sa réponse vaut des points ou non, elle est modérée ou
 * non. Tout le reste est commun, et l'était déjà dans la V1 sans qu'elle en tire
 * les conséquences : elle en fait cinq composants qui répètent le même cadre.
 *
 * ⚠️ CE COMPOSANT NE DÉCIDE DE RIEN. Il ne sait pas quelle est la bonne réponse
 * (le serveur ne la lui envoie pas avant les résultats), il ne sait pas si le vote
 * est encore ouvert autrement qu'en lisant la phase, et il n'empêche rien : il
 * rend gris ce que le serveur refusera de toute façon.
 *
 * ⚠️ CE QUE LIT UN PARTICIPANT PASSE PAR LE DICTIONNAIRE (`t()`), comme le reste
 * du mobilier du produit. Le direct était le seul écran dont les mots ne pouvaient
 * pas être changés depuis le back office, ni traduits : un événement anglophone y
 * lisait « Valider » et « Votre réponse est enregistrée » au milieu d'un site
 * traduit. Le repli reste le français, c'est la langue de référence.
 */

export function Activity({
  live,
  room = false,
  screenToken,
}: {
  live: LivePayload;
  /** Le jeton de l'écran de salle, que le classement doit porter pour se relire. */
  screenToken?: string | null;
  /**
   * ═══ SERVI À LA SALLE ═══
   *
   * ⚠️ LE MÊME MODULE, SANS CE QUI SE SAISIT. L'écran de salle réimplémentait
   * l'affichage des activités de son côté : deux rendus pour une même chose, donc
   * deux vérités le jour où l'une change. Ici c'est le même composant, privé de ses
   * champs et de ses boutons — personne ne vote sur un vidéoprojecteur, et un
   * champ de saisie sur un mur de dix mètres est une invitation à rien.
   */
  room?: boolean;
}) {
  const viewport = useViewport(room);
  /* ⚠️ UN RÉGLAGE, PLUS UN MOT DU PRODUIT (tâche #371) : « la régie » est le mot de
     l'organisateur, pas celui du participant, qui doit pouvoir lire « La session
     reprend dans un instant ». Le défaut reste la phrase d'avant, traduite sous la
     clé du réglage (`useText`). */
  const empty = useText(
    "FO_MEETING_V2_LIVE_NO_MODULE",
    fallbackOf("FO_MEETING_V2_LIVE_NO_MODULE"),
  );
  const activity = live.activity;
  if (!activity) {
    return <p className="empty">{empty}</p>;
  }

  /* Sur le mur, une longue liste de réponses passe en colonnes puis rétrécit, pour
     tenir dans un écran qui ne défile pas — calculé pour la fenêtre qu'on a, et
     recalculé quand elle change : voir `roomLayout.ts`. */
  const layout =
    room && CHOICE_TYPES.includes(activity.type)
      ? roomLayout(activity, smsVoting(live), viewport, live.results)
      : null;

  return (
    <div
      className="mod"
      data-mod={MOD_ATTR[activity.type]}
      style={
        layout
          ? ({ "--room-cols": layout.columns, "--room-k": layout.scale } as CSSProperties)
          : undefined
      }
    >
      <b>{activity.title || activity.content}</b>
      {activity.title && activity.content ? (
        <small className="hint">{activity.content}</small>
      ) : null}

      {/* ⚠️ LE CLASSEMENT NE LIT PAS `live` : il se relit par sa propre route, parce
          qu'il change à chaque bonne réponse de chaque personne et que le diffuser
          serait un message par vote. Voir `Podium.tsx`. */}
      {activity.type === "leaderboard" ? (
        <Podium room={room} screenToken={screenToken} />
      ) : CHOICE_TYPES.includes(activity.type) ? (
        <Choices live={live} room={room} />
      ) : activity.type === "cloud" ? (
        <Cloud live={live} room={room} />
      ) : (
        <Questions live={live} room={room} />
      )}
    </div>
  );
}

/**
 * LA TAILLE DE LA FENÊTRE, relue à chaque redimensionnement : le projecteur qu'on
 * branche, la fenêtre qu'on étire, la tablette qu'on tourne. Le téléphone ne s'abonne
 * pas — sa liste défile, elle n'a rien à recalculer.
 *
 * ⚠️ SUR LE MUR, LA PLACE ENTRE SES BANDEAUX, quand l'écran de salle l'a mesurée (voir
 * `roomBox.ts`) : c'est elle que la feuille remplit, pas la fenêtre.
 */
const noResize = () => () => {};
const onResize = (notify: () => void) => {
  window.addEventListener("resize", notify);
  return () => window.removeEventListener("resize", notify);
};

function useViewport(active: boolean): Viewport {
  const box = useContext(RoomBox);
  const subscribe = active && !box ? onResize : noResize;
  const width = useSyncExternalStore(subscribe, () => window.innerWidth);
  const height = useSyncExternalStore(subscribe, () => window.innerHeight);
  return active && box ? box : { width, height };
}

/** Sondage, quiz, vote : on choisit dans une liste. */
function Choices({ live, room }: { live: LivePayload; room: boolean }) {
  const activity = live.activity!;
  const max = Math.max(1, activity.config.maxAnswers ?? 1);
  const mine = answeredChoices(live);
  const answer = useAnswer(activity);

  const t = useT();
  const [picked, setPicked] = useState<number[]>([]);

  /* Une nouvelle activité efface la sélection : sans ça, les identifiants de la
     précédente restaient cochés et partaient au serveur, qui les refusait. */
  useEffect(() => {
    setPicked([]);
    answer.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id]);

  const done = mine.length > 0;
  const closed = activity.phase === "closed";

  /* Réception des SMS ouverte, la salle ET le téléphone numérotent les réponses et
     donnent la consigne ; le téléphone, lui, ne vote plus par ses boutons : voir
     `smsVote.ts`. Qui a déjà voté par ses boutons n'est pas invité à revoter par SMS. */
  const sms = smsVoting(live);
  const smsInstruction =
    sms.instruction && (room || !done) ? <SmsInstruction max={max} /> : null;

  if (activity.showResults) {
    return (
      <>
        <Results live={live} mine={mine} numbers={sms.numbers} />
        {smsInstruction}
      </>
    );
  }

  /** Les réponses possibles, qu'on regarde sans pouvoir en prendre. */
  const shown = (
    <>
      <div className="qz">
        {activity.choices.map((choice, index) => (
          <button key={choice.id} type="button" disabled>
            {sms.numbers ? <SmsNumber index={index} /> : null}
            {choice.image ? <img className="qz__i" src={choice.image} alt="" /> : null}
            {choice.label}
          </button>
        ))}
      </div>
      {smsInstruction}
    </>
  );

  /**
   * ⚠️ LA SALLE MONTRE LES RÉPONSES POSSIBLES, ELLE N'EN PREND PAS. Ni accusé de
   * réception ni « temps écoulé » : ce sont des messages adressés à quelqu'un, et
   * un mur ne s'adresse à personne.
   */
  if (room) return shown;

  if (done) {
    return (
      <p className="live__ack">
        {t("LIVE_ANSWER_SAVED", "Votre réponse est enregistrée. Merci.")}
      </p>
    );
  }

  if (closed) {
    return (
      <p className="live__ack">
        {t("LIVE_TIME_OVER", "Le temps imparti est écoulé, il n'est plus possible de répondre.")}
      </p>
    );
  }

  /* ⚠️ RÉCEPTION DES SMS OUVERTE : LECTURE SEULE (tâche #367). On vote par SMS, plus
     par les boutons — sinon chacun pourrait voter deux fois. Après l'accusé de
     réception, pour la même raison que la consigne ci-dessus. */
  if (sms.numbers) return shown;

  const toggle = (id: number) => {
    setPicked((current) => {
      if (current.includes(id)) return current.filter((c) => c !== id);
      /* Au-delà du maximum, la plus ancienne cède sa place plutôt que de bloquer :
         un participant qui change d'avis sur un choix unique ne doit pas avoir à
         décocher d'abord. C'est le geste de la V1, gardé. */
      if (current.length >= max) return [...current.slice(1), id];
      return [...current, id];
    });
  };

  return (
    <>
      {max > 1 ? (
        <small className="hint">
          {picked.length} / {max}{" "}
          {/* Préfixées `LIVE_` comme les autres : `ANSWER` tout court est un nom
              qu'on peut vouloir pour un bouton « Répondre », et le dictionnaire est
              partagé par tout le produit. */}
          {max > 1 ? t("LIVE_ANSWERS", "réponses") : t("LIVE_ANSWER", "réponse")}
        </small>
      ) : null}

      {/* ⚠️ TOUT LE BLOC PART EN ATTENTE, pas seulement le bouton. Le serveur met
          jusqu'à deux secondes à accepter un vote, et pendant ce temps le seul signe
          était un rouage dans « Valider » : la liste restait franche et cliquable, donc
          l'écran avait l'air de n'avoir rien fait. Signalé tel quel. */}
      <div className="qz" data-sending={answer.isPending ? "1" : undefined}>
        {activity.choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            disabled={answer.isPending}
            aria-pressed={picked.includes(choice.id)}
            className={picked.includes(choice.id) ? "qz--on" : undefined}
            onClick={() => toggle(choice.id)}
          >
            {choice.image ? (
              <img className="qz__i" src={choice.image} alt="" />
            ) : null}
            {choice.label}
          </button>
        ))}
      </div>

      {answer.isError ? (
        <p className="live__err" role="alert">
          {refusalOf(answer.error)}
        </p>
      ) : null}

      <Button
        loading={answer.isPending}
        disabled={picked.length === 0}
        onClick={() => answer.mutate({ choiceIds: picked })}
      >
        {t("VALIDATE", "Valider")}
      </Button>
    </>
  );
}

/**
 * ═══ LA CONSIGNE DU VOTE PAR SMS, SUR LE MUR ET LES TÉLÉPHONES ═══
 *
 * Le numéro en grand : c'est ce qu'on recopie de loin. Il se règle sur l'écran de salle
 * (`FO_MEETING_V2_LIVE_SMS_NUMBER`) ; sans lui la consigne reste, et l'organisation
 * donne le numéro de vive voix.
 */
function SmsInstruction({ max }: { max: number }) {
  const t = useT();
  const raw = useStructuralSetting("FO_MEETING_V2_LIVE_SMS_NUMBER");
  const number = typeof raw === "string" ? raw.trim() : "";

  return (
    <p className="smsv">
      <span>{t("LIVE_SMS_VOTE", "Votez par SMS")}</span>
      {number ? <b className="smsv__n">{number}</b> : null}
      <small>
        {/* Des clés neuves, pas les anciennes reformulées : déjà semées, celles-ci
            diraient encore « la lettre » (voir `i18n/README.md`, « un mot qui change »). */}
        {max > 1
          ? t("LIVE_SMS_VOTE_NUMBERS_MANY", "Envoyez les numéros de vos réponses, $1 au plus.", [max])
          : t("LIVE_SMS_VOTE_NUMBER_ONE", "Envoyez le numéro de votre réponse.")}
      </small>
    </p>
  );
}

/** Nuage de mots : un mot, et le nuage qui se remplit. */
function Cloud({ live, room }: { live: LivePayload; room: boolean }) {
  const activity = live.activity!;
  const t = useT();
  const answer = useAnswer(activity);
  const [word, setWord] = useState("");

  /* Comme pour les choix : le composant n'est PAS remonté d'une activité à
     l'autre, donc rien ne se vide tout seul. Sans ça, le refus du nuage précédent
     s'affichait sous la question du suivant, avec le texte non envoyé encore dans
     le champ, prêt à partir sur la mauvaise activité. */
  useEffect(() => {
    setWord("");
    answer.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id]);

  const done = (live.myAnswers ?? []).length > 0;
  const closed = activity.phase === "closed";
  const cloud = live.cloud ?? [];

  return (
    <>
      {!room && !done && !closed ? (
        <>
          <div className="f">
            <label htmlFor="live-word">{t("LIVE_WRITE_A_WORD", "Écrivez un mot")}</label>
            <input
              id="live-word"
              type="text"
              value={word}
              maxLength={60}
              autoComplete="off"
              disabled={answer.isPending}
              onChange={(e) => setWord(e.target.value)}
            />
          </div>
          {answer.isError ? (
            <p className="live__err" role="alert">
              {refusalOf(answer.error)}
            </p>
          ) : null}
          <Button
            loading={answer.isPending}
            disabled={!word.trim()}
            onClick={() =>
              answer.mutate({ text: word.trim() }, { onSuccess: () => setWord("") })
            }
          >
            {t("SEND", "Envoyer")}
          </Button>
        </>
      ) : room ? null : (
        <p className="live__ack">
          {closed
            ? t("LIVE_TIME_OVER_SHORT", "Le temps imparti est écoulé.")
            : t("LIVE_WORD_SAVED", "Votre mot est pris en compte. Merci.")}
        </p>
      )}

      {/* Le nuage se remplit sous les yeux : c'est ce qui en fait un nuage. La
          taille suit le nombre de fois où le mot a été dit. */}
      <div className="cloud">
        {cloud.map((word_) => (
          <span
            key={word_.text}
            className="cloud__w"
            /**
             * ⚠️ LA TAILLE SE DÉCIDE ICI, PAS EN CSS, et il faut donc que la SALLE
             * soit dite ici aussi. Une règle `.scene__mod .cloud__w { font-size:
             * 2em }` avait été écrite pour doubler la projection : un style en
             * ligne bat toujours une feuille, et le mur affichait donc des mots de
             * 14 à 40 pixels, à dix mètres.
             *
             * Le facteur est 3 et non 2 : mesuré sur une carte de 1560 x 670, le
             * double laissait le nuage sur une seule ligne au milieu d'un grand
             * vide. Un nuage doit remplir sa surface, c'est ce qui en fait un nuage.
             */
            style={{ fontSize: `${weightOf(word_.count, cloud) * (room ? 3 : 1)}px` }}
          >
            {word_.text}
          </span>
        ))}
      </div>
    </>
  );
}

/**
 * LA TAILLE D'UN MOT, entre 14 et 40 pixels.
 *
 * ⚠️ RELATIVE AU PLUS DIT, pas au nombre brut. Sur un nuage où tout le monde dit
 * des mots différents, chaque mot vaut 1 et une échelle absolue les rendrait tous
 * minuscules — le nuage aurait l'air vide alors qu'il est plein.
 */
export function weightOf(count: number, all: { count: number }[]): number {
  const top = all.reduce((max, w) => Math.max(max, w.count), 1);
  if (top <= 1) return 22;
  return Math.round(14 + (26 * (count - 1)) / (top - 1));
}

/** Questions du public : on écrit, la régie décide de ce qui s'affiche. */
function Questions({ live, room }: { live: LivePayload; room: boolean }) {
  const activity = live.activity!;
  const t = useT();
  const answer = useAnswer(activity);
  const [text, setText] = useState("");

  /* Même raison que pour le nuage : pas de remontage, donc pas de remise à zéro. */
  useEffect(() => {
    setText("");
    answer.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id]);

  const moderated = activity.config.moderation !== false;
  const sent = (live.myAnswers ?? []).length > 0;
  const closed = activity.phase === "closed";

  /**
   * ═══ LE MUR NE MONTRE QUE CE QUE LA RÉGIE Y MET ═══
   *
   * ⚠️ « RETIRER DE L'ÉCRAN » DOIT RETIRER DE L'ÉCRAN. La salle affichait la LISTE
   * des questions validées, et la mise à l'écran ne faisait qu'en agrandir une :
   * la retirer la renvoyait dans la liste, donc elle restait visible. Le geste
   * n'avait pas l'effet que son nom annonce, et l'organisateur reclique.
   *
   * ⚠️ ET UNE LISTE N'EST PAS UNE PROJECTION. À dix mètres, cinq questions en
   * petits caractères ne se lisent pas ; une seule, en grand, se lit. C'est aussi
   * ce que la régie décide : elle choisit la question que la salle lit.
   *
   * Sans question à l'écran, le mur le dit sobrement plutôt que d'afficher un
   * inventaire que personne ne peut lire.
   */
  if (room) {
    return live.spotlight ? (
      <div className="stage__q">{live.spotlight.text}</div>
    ) : (
      <p className="scene__wait">
        {t("LIVE_QUESTIONS_COMING", "Les questions arrivent.")}
      </p>
    );
  }

  return (
    <>
      <div className="qlist">
        {(live.answers ?? []).map((question) => (
          <div key={question.id} className="qitem">
            <span>{question.text}</span>
          </div>
        ))}
        {(live.answers ?? []).length === 0 ? (
          <p className="empty">{t("LIVE_NO_QUESTION_YET", "Aucune question pour l'instant.")}</p>
        ) : null}
      </div>

      {!room && !closed ? (
        <>
          <div className="f">
            <label htmlFor="live-question">{t("LIVE_ASK_A_QUESTION", "Poser une question")}</label>
            <input
              id="live-question"
              type="text"
              value={text}
              maxLength={500}
              autoComplete="off"
              disabled={answer.isPending}
              placeholder={t("LIVE_YOUR_QUESTION", "Votre question")}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          {answer.isError ? (
            <p className="live__err" role="alert">
              {refusalOf(answer.error)}
            </p>
          ) : null}
          <Button
            loading={answer.isPending}
            disabled={!text.trim()}
            onClick={() =>
              answer.mutate({ text: text.trim() }, { onSuccess: () => setText("") })
            }
          >
            {t("SEND", "Envoyer")}
          </Button>
          {moderated && sent ? (
            <small className="hint">
              {t(
                "LIVE_QUESTION_SENT",
                "Votre question a été transmise : elle s'affichera une fois validée par la régie.",
              )}
            </small>
          ) : null}
        </>
      ) : room ? null : (
        <p className="live__ack">{t("LIVE_QUESTIONS_CLOSED", "Les questions sont closes.")}</p>
      )}
    </>
  );
}
